// Multi-tab session coherence (web only).
//
// Every tab in one browser shares ONE server session: the httpOnly reissue cookie is per-browser, and each
// tab holds that session's bearer in memory. `POST /auth/reissue` ROTATES the session's bearer in place
// (`SessionService.RotateAsync` — the previous bearer stops working immediately), so a second tab booting,
// or reloading, killed every other tab's bearer: their next request 401'd and the clear-on-401 middleware
// logged them out. "The first tab logs itself out."
//
// Three pieces, each covering a hole the others leave:
//   • BROADCAST — whichever tab reissues posts the fresh bearer to the others, so they adopt it instead of
//     holding a dead one. This is the common path, and it costs no round trip.
//   • LOCK — reissues are serialized across tabs with a Web Lock. The cookie is single-use, so two tabs
//     reissuing at once race: both send the same cookie, one wins, the loser 401s and is logged out. Inside
//     the lock a tab first checks whether another tab already rotated while it waited, and adopts that.
//   • RECOVER — a 401 on an ordinary route gets ONE reissue-and-retry before it is treated as a real logout
//     (the API middleware calls `recoverBearer`). Covers a missed broadcast, a browser without
//     BroadcastChannel, or a request that was already in flight when the rotation happened.
//
// The bearer only ever travels between same-origin tabs in memory — never storage (the lint-enforced rule).
// The server's single-use rotation is untouched.
import type { ApiClient } from "@cntrl/api-client";
import type { TokenProvider } from "@cntrl/native";
import { useDevImpersonation } from "../devImpersonation.js";

const CHANNEL_NAME = "cntrl-session";
const LOCK_NAME = "cntrl-session-reissue";

interface BearerMessage {
  type: "bearer";
  token: string;
}

function openChannel(): BroadcastChannel | null {
  return typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHANNEL_NAME);
}

/** Run `fn` holding the cross-tab reissue lock; falls back to running it unlocked where Web Locks are absent. */
async function withReissueLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks) return fn();
  return locks.request(LOCK_NAME, fn) as Promise<T>;
}

/**
 * Adopt a bearer another tab reissued. An impersonating tab (dev fixture switcher) is WEARING a different
 * session, so the rotated real bearer replaces only the one it holds for the return trip — adopting it as the
 * current bearer would silently drop the swap.
 */
async function adopt(tokenProvider: TokenProvider, token: string): Promise<void> {
  const swap = useDevImpersonation.getState();
  if (swap.actingAs) {
    useDevImpersonation.setState({ ownBearer: token });
    return;
  }
  await tokenProvider.setBearer(token);
}

export interface SessionSync {
  /**
   * POST /auth/reissue under the cross-tab lock, adopt + broadcast the result. Resolves the new bearer, or
   * null when there is no session to resume (401 / network). `staleBearer` is the bearer the caller already
   * knows is dead: if another tab replaced it while this one waited for the lock, that one is returned and
   * no reissue is sent.
   */
  reissue(staleBearer?: string | null): Promise<string | null>;
  /** Stop listening (tests / teardown). */
  close(): void;
}

export function createSessionSync(api: ApiClient, tokenProvider: TokenProvider): SessionSync {
  const channel = openChannel();

  if (channel) {
    channel.onmessage = (event: MessageEvent<BearerMessage>) => {
      const msg = event.data;
      if (msg?.type !== "bearer" || typeof msg.token !== "string") return;
      // A signed-out tab (sitting on /login) does not get signed back in by a neighbour.
      if (!tokenProvider.isAuthenticated()) return;
      void adopt(tokenProvider, msg.token);
    };
  }

  // Single-flight within the tab: a burst of parallel 401s shares one reissue.
  let inFlight: Promise<string | null> | null = null;

  const reissue = (staleBearer?: string | null): Promise<string | null> => {
    inFlight ??= withReissueLock(async () => {
      const current = tokenProvider.getBearer();
      if (staleBearer && current && current !== staleBearer) return current;   // a neighbour already rotated

      try {
        const { data, response } = await api.POST("/api/v1/auth/reissue", {});
        if (!response.ok || !data?.token) return null;
        await tokenProvider.setBearer(data.token);
        channel?.postMessage({ type: "bearer", token: data.token } satisfies BearerMessage);
        return data.token;
      } catch {
        return null;   // network failure is not a session verdict
      }
    }).finally(() => {
      inFlight = null;
    });
    return inFlight;
  };

  return {
    reissue,
    close: () => channel?.close(),
  };
}
